import { logger } from 'src/analytics/KatalLogger';
import { ForecastGridRowData, ForecastTemplateColumns } from 'src/models/ForecastModels';
import { AccountBudgetTypeMapping, CompanyCurrencyMapping } from 'src/models/xPTMappingModels';
import { ForecastGridFixedFields } from '../forecast-utils/ForecastGridConstants';
import { getCurrentUTCTimeInISO } from 'src/utils/date-time-utilities';
import { generateUniqueId } from 'src/utils/generic-utilities';
import { isSegmentModified } from '../forecast-utils/ForecastTemplateUtils';
import { CorpSegmentNames } from 'src/constants/corp-segment-constants';

/**
 * Service for transforming data in forecast templates
 */
export class DataTransformationService {
  /**
   * Transforms the display header format to the model format for uploaded files.
   * @param fileData The array of data objects from the file.
   * @param forecastTemplateColumns The forecast template columns configuration.
   * @returns The transformed data.
   */
  public transformUploadFileDisplayHeaderToModel(
    fileData: ForecastGridRowData[],
    forecastTemplateColumns: ForecastTemplateColumns
  ): ForecastGridRowData[] {
    return fileData.map((item) => this.transformSingleRowHeaderToModel(item, forecastTemplateColumns));
  }

  /**
   * Transforms a single row from display header format to model format.
   * @param item The row data to transform.
   * @param forecastTemplateColumns The forecast template columns configuration.
   * @returns The transformed row data.
   */
  private transformSingleRowHeaderToModel(item: ForecastGridRowData, forecastTemplateColumns: ForecastTemplateColumns): ForecastGridRowData {
    const transformedItem: Record<string, any> = {};

    // Transform fixed fields
    this.transformFixedFields(item, transformedItem);

    // Transform month fields
    this.transformMonthFields(item, transformedItem, forecastTemplateColumns);

    // Include remaining untransformed fields
    this.includeRemainingFields(item, transformedItem, forecastTemplateColumns);

    return transformedItem as ForecastGridRowData;
  }

  /**
   * Transforms fixed forecast fields from display format to model format.
   * @param item The source item.
   * @param transformedItem The target transformed item.
   */
  private transformFixedFields(item: ForecastGridRowData, transformedItem: Record<string, any>): void {
    const fieldMappings = [
      { display: ForecastGridFixedFields.XptLineItemId.displayName, value: ForecastGridFixedFields.XptLineItemId.value },
      { display: ForecastGridFixedFields.BudgetOwner.displayName, value: ForecastGridFixedFields.BudgetOwner.value },
      { display: ForecastGridFixedFields.BudgetType.displayName, value: ForecastGridFixedFields.BudgetType.value },
      { display: ForecastGridFixedFields.LOCAL_CURRENCY.displayName, value: ForecastGridFixedFields.LOCAL_CURRENCY.value },
      { display: ForecastGridFixedFields.IS_PLANNED_IN_USD.displayName, value: ForecastGridFixedFields.IS_PLANNED_IN_USD.value }
    ];

    fieldMappings.forEach(({ display, value }) => {
      transformedItem[value] = item[display];
    });
  }

  /**
   * Transforms month fields from display format to model format.
   * @param item The source item.
   * @param transformedItem The target transformed item.
   * @param forecastTemplateColumns The forecast template columns configuration.
   */
  private transformMonthFields(
    item: ForecastGridRowData,
    transformedItem: Record<string, any>,
    forecastTemplateColumns: ForecastTemplateColumns
  ): void {
    const { forecastMonthsDisplayFormat, forecastMonthColumnsIds, actualMonthsDisplayFormat, actualMonthColumnIds } = forecastTemplateColumns;

    // Transform forecast months
    forecastMonthsDisplayFormat.forEach((displayField, index) => {
      transformedItem[forecastMonthColumnsIds[index]] = item[displayField];
    });

    // Transform actual months
    actualMonthsDisplayFormat.forEach((displayField, index) => {
      transformedItem[actualMonthColumnIds[index]] = item[displayField];
    });
  }

  /**
   * Includes remaining fields that are not explicitly transformed.
   * @param item The source item.
   * @param transformedItem The target transformed item.
   * @param forecastTemplateColumns The forecast template columns configuration.
   */
  private includeRemainingFields(
    item: ForecastGridRowData,
    transformedItem: Record<string, any>,
    forecastTemplateColumns: ForecastTemplateColumns
  ): void {
    const transformedFields = [
      ForecastGridFixedFields.XptLineItemId.displayName,
      ForecastGridFixedFields.BudgetOwner.displayName,
      ForecastGridFixedFields.BudgetType.displayName,
      ForecastGridFixedFields.LOCAL_CURRENCY.displayName,
      ForecastGridFixedFields.IS_PLANNED_IN_USD.displayName,
      ...forecastTemplateColumns.forecastMonthsDisplayFormat,
      ...forecastTemplateColumns.actualMonthsDisplayFormat
    ];

    Object.keys(item).forEach((key) => {
      if (!transformedFields.includes(key)) {
        transformedItem[key] = item[key];
      }
    });
  }

  /**
   * Gets the modified rows from the Excel file by comparing with the complete data.
   * @param forecastTemplateColumns The forecast template columns configuration.
   * @param transformedUploadFileData The transformed data from the uploaded file.
   * @param forecastTemplateCompleteData The complete data from the forecast template.
   * @param userAlias The alias of the current user.
   * @param scenarioSeqId The scenario sequence ID.
   * @param accountBudgetTypeMapping The mapping of accounts to budget types.
   * @returns The array of modified rows.
   */
  public getModifiedRowsFromExcelFile(
    forecastTemplateColumns: ForecastTemplateColumns,
    transformedUploadFileData: ForecastGridRowData[],
    forecastTemplateCompleteData: any[],
    userAlias: string,
    scenarioSeqId: number | null,
    accountBudgetTypeMapping: AccountBudgetTypeMapping[],
    companyCurrencyMapping: CompanyCurrencyMapping[]
  ): ForecastGridRowData[] {
    const modifiedRows: ForecastGridRowData[] = [];

    const forecastMonthColumnsIds = forecastTemplateColumns.forecastMonthColumnsIds;
    const corpSegmentFields = forecastTemplateColumns.corpSegmentMandatoryFields;

    try {
      transformedUploadFileData.forEach((fileRow) => {
        const isNewRow = !fileRow[ForecastGridFixedFields.XptLineItemId.value];

        if (isNewRow) {
          // Handle new row
          const processedRow = this.processNewRow(fileRow, userAlias, scenarioSeqId, accountBudgetTypeMapping, companyCurrencyMapping);
          if (processedRow) {
            modifiedRows.push(processedRow);
          }
        } else {
          // Handle existing row
          const processedRow = this.processExistingRow(
            fileRow,
            forecastTemplateCompleteData,
            forecastTemplateColumns,
            forecastMonthColumnsIds,
            corpSegmentFields,
            userAlias,
            scenarioSeqId,
            accountBudgetTypeMapping,
            companyCurrencyMapping
          );
          if (processedRow) {
            modifiedRows.push(processedRow);
          }
        }
      });
    } catch (error: any) {
      logger.error('Error while comparing rows:', error);
    }

    return modifiedRows;
  }

  /**
   * Processes a new row from the uploaded file.
   * @param fileRow The row data from the file.
   * @param userAlias The alias of the current user.
   * @param scenarioSeqId The scenario sequence ID.
   * @param accountBudgetTypeMapping The mapping of accounts to budget types.
   * @param companyCurrencyMapping The mapping of companies to currencies.
   * @returns The processed row data or null if processing failed.
   */
  private processNewRow(
    fileRow: ForecastGridRowData,
    userAlias: string,
    scenarioSeqId: number | null,
    accountBudgetTypeMapping: AccountBudgetTypeMapping[],
    companyCurrencyMapping: CompanyCurrencyMapping[]
  ): ForecastGridRowData | null {
    const correspondingBudgetType = this.findBudgetTypeForAccount(fileRow, accountBudgetTypeMapping, 'warn');
    const correspondingCurrency = this.findCurrencyForCompany(fileRow, companyCurrencyMapping, 'warn');

    const baseRow = this.createBaseRowFields(fileRow, userAlias, scenarioSeqId, correspondingBudgetType, correspondingCurrency);
    const newRowFields = this.createNewRowFields();

    return { ...baseRow, ...newRowFields } as ForecastGridRowData;
  }

  /**
   * Creates base row fields common to both new and existing rows.
   * @param fileRow The source file row.
   * @param userAlias The user alias.
   * @param scenarioSeqId The scenario sequence ID.
   * @param budgetType The budget type to set.
   * @param currency The currency to set.
   * @returns The base row fields.
   */
  private createBaseRowFields(
    fileRow: ForecastGridRowData,
    userAlias: string,
    scenarioSeqId: number | null,
    budgetType: string | null,
    currency: string | null
  ): Record<string, any> {
    return {
      ...fileRow,
      [ForecastGridFixedFields.RowId.value]: generateUniqueId(),
      [ForecastGridFixedFields.ScenarioSeqId.value]: scenarioSeqId,
      [ForecastGridFixedFields.IsTouched.value]: true,
      [ForecastGridFixedFields.IsEdited.value]: true,
      [ForecastGridFixedFields.IsActive.value]: true,
      [ForecastGridFixedFields.BudgetType.value]: budgetType,
      [ForecastGridFixedFields.LOCAL_CURRENCY.value]: currency,
      [ForecastGridFixedFields.UpdatedBy.value]: userAlias,
      [ForecastGridFixedFields.UpdatedAt.value]: getCurrentUTCTimeInISO()
    };
  }

  /**
   * Creates fields specific to new rows.
   * @returns The new row specific fields.
   */
  private createNewRowFields(): Record<string, any> {
    return {
      [ForecastGridFixedFields.XptLineItemId.value]: null,
      [ForecastGridFixedFields.XptLineItemSeqId.value]: null,
      [ForecastGridFixedFields.ForecastInputId.value]: null,
      [ForecastGridFixedFields.IsNew.value]: true,
      [ForecastGridFixedFields.IsSegmentEdited.value]: true,
      [ForecastGridFixedFields.IsNewFERow.value]: true
    };
  }

  /**
   * Finds the budget type for a given account code.
   * @param fileRow The file row containing the account code.
   * @param accountBudgetTypeMapping The account to budget type mappings.
   * @param logLevel The log level to use when budget type is not found.
   * @returns The budget type or null if not found.
   */
  private findBudgetTypeForAccount(
    fileRow: ForecastGridRowData,
    accountBudgetTypeMapping: AccountBudgetTypeMapping[],
    logLevel: 'warn' | 'error'
  ): string | null {
    const accountCode = `${fileRow[CorpSegmentNames.ACCOUNT]}`;
    const budgetType = accountBudgetTypeMapping.find((mapping) => mapping.account_code === accountCode)?.budget_type || null;

    if (budgetType === null) {
      const message = `Unable to find Budget Type for Account Code ${accountCode}`;
      logLevel === 'warn' ? logger.warn(message) : logger.error(message);
    }

    return budgetType;
  }

  /**
   * Finds the currency for a given company code.
   * @param fileRow The file row containing the company code.
   * @param companyCurrencyMapping The company to currency mappings.
   * @param logLevel The log level to use when currency is not found.
   * @returns The currency or null if not found.
   * @feature Currency (Local / USD)
   * @since 2025.1.0
   */
  private findCurrencyForCompany(
    fileRow: ForecastGridRowData,
    companyCurrencyMapping: CompanyCurrencyMapping[],
    logLevel: 'warn' | 'error'
  ): string | null {
    const companyCode = `${fileRow[CorpSegmentNames.COMPANY]}`;
    const currency = companyCurrencyMapping.find((mapping) => mapping.company_code === companyCode)?.currency || null;

    if (currency === null) {
      const message = `Unable to find Currency for Company Code ${companyCode}`;
      logLevel === 'warn' ? logger.warn(message) : logger.error(message);
    }

    return currency;
  }

  /**
   * Processes an existing row from the uploaded file.
   * @param fileRow The row data from the file.
   * @param forecastTemplateCompleteData The complete data from the forecast template.
   * @param forecastTemplateColumns The forecast template columns configuration.
   * @param forecastMonthColumnsIds The array of forecast month column IDs.
   * @param corpSegmentFields The array of corp segment fields.
   * @param userAlias The alias of the current user.
   * @param scenarioSeqId The scenario sequence ID.
   * @param accountBudgetTypeMapping The mapping of accounts to budget types.
   * @param companyCurrencyMapping The mapping of companies to currencies.
   * @returns The processed row data if modified, or null if not modified.
   */
  private processExistingRow(
    fileRow: ForecastGridRowData,
    forecastTemplateCompleteData: any[],
    forecastTemplateColumns: ForecastTemplateColumns,
    forecastMonthColumnsIds: string[],
    corpSegmentFields: string[],
    userAlias: string,
    scenarioSeqId: number | null,
    accountBudgetTypeMapping: AccountBudgetTypeMapping[],
    companyCurrencyMapping: CompanyCurrencyMapping[]
  ): ForecastGridRowData | null {
    const correspondingRow = this.findCorrespondingRow(fileRow, forecastTemplateCompleteData);

    if (!correspondingRow) {
      throw new Error(`No corresponding row found for line item id ${fileRow[ForecastGridFixedFields.XptLineItemId.value]}`);
    }

    const isModified = this.isRowModified(fileRow, correspondingRow, forecastTemplateColumns, forecastMonthColumnsIds, corpSegmentFields);

    if (isModified) {
      return this.createModifiedExistingRow(
        fileRow,
        correspondingRow,
        forecastTemplateColumns,
        userAlias,
        scenarioSeqId,
        accountBudgetTypeMapping,
        companyCurrencyMapping
      );
    }

    return null;
  }

  /**
   * Finds the corresponding row in the complete data based on line item ID.
   * @param fileRow The file row to find a match for.
   * @param forecastTemplateCompleteData The complete data to search in.
   * @returns The corresponding row or undefined if not found.
   */
  private findCorrespondingRow(fileRow: ForecastGridRowData, forecastTemplateCompleteData: any[]): any {
    return forecastTemplateCompleteData.find(
      (templateRow) => `${templateRow[ForecastGridFixedFields.XptLineItemId.value]}` === `${fileRow[ForecastGridFixedFields.XptLineItemId.value]}`
    );
  }

  /**
   * Checks if a row has been modified by comparing file data with template data.
   * @param fileRow The file row data.
   * @param correspondingRow The corresponding template row data.
   * @param forecastTemplateColumns The forecast template columns configuration.
   * @param forecastMonthColumnsIds The forecast month column IDs.
   * @param corpSegmentFields The corp segment fields.
   * @returns True if the row has been modified, false otherwise.
   */
  private isRowModified(
    fileRow: ForecastGridRowData,
    correspondingRow: any,
    forecastTemplateColumns: ForecastTemplateColumns,
    forecastMonthColumnsIds: string[],
    corpSegmentFields: string[]
  ): boolean {
    for (const checkField of forecastTemplateColumns.modifiedCheckFields) {
      const { fileValue, templateValue } = this.normalizeFieldValues(
        fileRow[checkField],
        correspondingRow[checkField],
        checkField,
        forecastMonthColumnsIds,
        corpSegmentFields
      );

      if (`${fileValue}` !== `${templateValue}`) {
        console.debug(
          `Modified field with line item id as ${
            correspondingRow[ForecastGridFixedFields.XptLineItemId.value]
          }: ${checkField}, File Value: ${fileValue}, Template Value: ${templateValue}`
        );
        return true;
      }
    }
    return false;
  }

  /**
   * Normalizes field values for comparison based on field type.
   * @param rawFileValue The raw value from the file.
   * @param rawTemplateValue The raw value from the template.
   * @param fieldName The name of the field being compared.
   * @param forecastMonthColumnsIds The forecast month column IDs.
   * @param corpSegmentFields The corp segment fields.
   * @returns Normalized file and template values.
   */
  private normalizeFieldValues(
    rawFileValue: any,
    rawTemplateValue: any,
    fieldName: string,
    forecastMonthColumnsIds: string[],
    corpSegmentFields: string[]
  ): { fileValue: any; templateValue: any } {
    let fileValue = rawFileValue === '' || rawFileValue === undefined ? null : rawFileValue;
    let templateValue = rawTemplateValue === 0 || rawTemplateValue ? rawTemplateValue : null;

    // Convert corp segment values to strings
    if (corpSegmentFields.includes(fieldName)) {
      fileValue = fileValue?.toString() ?? fileValue;
      templateValue = templateValue?.toString() ?? templateValue;
    }

    // Handle forecast month values
    if (forecastMonthColumnsIds.includes(fieldName)) {
      fileValue = fileValue !== null ? parseFloat(fileValue) : fileValue;
      templateValue = templateValue !== null ? parseFloat(templateValue) : templateValue;
    }

    return { fileValue, templateValue };
  }

  /**
   * Creates a modified existing row with updated mappings and metadata.
   * @param fileRow The file row data.
   * @param correspondingRow The corresponding template row data.
   * @param forecastTemplateColumns The forecast template columns configuration.
   * @param userAlias The user alias.
   * @param scenarioSeqId The scenario sequence ID.
   * @param accountBudgetTypeMapping The account budget type mappings.
   * @param companyCurrencyMapping The company currency mappings.
   * @returns The modified row data.
   */
  private createModifiedExistingRow(
    fileRow: ForecastGridRowData,
    correspondingRow: any,
    forecastTemplateColumns: ForecastTemplateColumns,
    userAlias: string,
    scenarioSeqId: number | null,
    accountBudgetTypeMapping: AccountBudgetTypeMapping[],
    companyCurrencyMapping: CompanyCurrencyMapping[]
  ): ForecastGridRowData {
    const isSegmentEdited = isSegmentModified(correspondingRow, fileRow, forecastTemplateColumns.corpAndBussSegmentsMandatoryFields);

    const correspondingBudgetType = this.findBudgetTypeForAccount(fileRow, accountBudgetTypeMapping, 'error');
    const correspondingCurrency = this.findCurrencyForCompany(fileRow, companyCurrencyMapping, 'error');

    const baseRow = this.createBaseRowFields(fileRow, userAlias, scenarioSeqId, correspondingBudgetType, correspondingCurrency);
    const existingRowFields = this.createExistingRowFields(correspondingRow, isSegmentEdited);

    return { ...baseRow, ...existingRowFields } as ForecastGridRowData;
  }

  /**
   * Creates fields specific to existing rows.
   * @param correspondingRow The corresponding template row.
   * @param isSegmentEdited Whether the segment has been edited.
   * @returns The existing row specific fields.
   */
  private createExistingRowFields(correspondingRow: any, isSegmentEdited: boolean): Record<string, any> {
    return {
      [ForecastGridFixedFields.XptLineItemId.value]: correspondingRow[ForecastGridFixedFields.XptLineItemId.value],
      [ForecastGridFixedFields.XptLineItemSeqId.value]: correspondingRow[ForecastGridFixedFields.XptLineItemSeqId.value],
      [ForecastGridFixedFields.ForecastInputId.value]: correspondingRow[ForecastGridFixedFields.ForecastInputId.value],
      [ForecastGridFixedFields.IsNew.value]: correspondingRow[ForecastGridFixedFields.IsNew.value],
      [ForecastGridFixedFields.IsSegmentEdited.value]: isSegmentEdited,
      [ForecastGridFixedFields.IsNewFERow.value]: false
    };
  }
}

// Export singleton instance
export const dataTransformationService = new DataTransformationService();
